/**
 * File: functionsUrl.ts
 * Path: packages/config/src/firebase/functionsUrl.ts
 * Description: The one place that knows which region Cloud Functions live in.
 *
 * Every function handler, every hosting rewrite in firebase.json and every
 * client URL builder reads this single constant, so the deployment can never
 * disagree with the callers about where a function is.
 */

/// <reference types="vite/client" />

/**
 * The region every Cloud Function is deployed to.
 *
 * Deliberately NOT the region Firestore is in (firebase.json →
 * firestore.location is africa-south1), so every rate-limit transaction and
 * delivery write pays a round trip. That is a chosen trade, not an oversight —
 * do not "fix" it by editing this constant alone.
 *
 * Changing it means changing the `region` on all four hosting rewrites in
 * firebase.json in the same commit; the `**` rewrite serves every page of the
 * website, so a mismatch takes the whole site down rather than just the tool
 * endpoints. apps/server/src/modules/functions/functionRegions.test.ts fails when the
 * two disagree.
 *
 * The one function that does not follow this constant is the Cloud Storage
 * trigger (onAuthoritativeImageUpload): an Eventarc storage trigger must run in
 * the bucket's own region, and the bucket's location is fixed at creation.
 */
export const FUNCTIONS_REGION = "europe-west1";

/** @deprecated Use {@link FUNCTIONS_REGION}; kept so older imports keep resolving. */
export const HTTP_FUNCTIONS_REGION = FUNCTIONS_REGION;
/** Port of the local Functions emulator (firebase.json → emulators.functions.port). */
export const FUNCTIONS_EMULATOR_PORT = 5001;
export const DEFAULT_PROJECT_ID = "idk-solutions";

export interface HttpFunctionUrlContext {
  /** Local development: route to the Functions emulator. */
  isDev: boolean;
  projectId: string;
  /**
   * True when the calling app is served by Firebase Hosting with rewrites for
   * the function (the website). False for apps without rewrites (the admin).
   */
  sameOrigin: boolean;
}

/** Pure URL builder — unit-testable without a browser. */
export const buildHttpFunctionUrl = (
  functionName: string,
  ctx: HttpFunctionUrlContext,
): string => {
  if (ctx.isDev) {
    return `http://127.0.0.1:${FUNCTIONS_EMULATOR_PORT}/${ctx.projectId}/${FUNCTIONS_REGION}/${functionName}`;
  }
  if (ctx.sameOrigin) return `/${functionName}`;
  return `https://${FUNCTIONS_REGION}-${ctx.projectId}.cloudfunctions.net/${functionName}`;
};

const isBrowser = (): boolean => typeof window !== "undefined";

const detectDev = (): boolean => {
  if (!isBrowser()) return false;
  const env = (import.meta.env ?? {}) as Record<string, unknown>;
  if (env.DEV === true || env.VITE_USE_EMULATORS === "true") return true;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
};

/**
 * Resolves the URL for a raw `fetch` to an HTTP Cloud Function from the
 * current runtime. `sameOrigin` must be true only where hosting rewrites exist.
 */
export const resolveHttpFunctionUrl = (
  functionName: string,
  opts: { sameOrigin: boolean },
): string =>
  buildHttpFunctionUrl(functionName, {
    isDev: detectDev(),
    projectId:
      (import.meta.env?.VITE_FIREBASE_PROJECT_ID as string | undefined) ||
      DEFAULT_PROJECT_ID,
    sameOrigin: opts.sameOrigin,
  });
